const STALE_AFTER_MS = 24 * 60 * 60 * 1000;
const LEGACY_FALLBACK_COPY =
  "Recent RiskFlow input cleared the signal threshold before the AI refinement layer produced a card.";
const PENDING_REFINEMENT_COPY =
  "Pending Agentic Desk refinement. This catalyst met the desk-watch threshold and needs Herald/CAO synthesis before it should be treated as a full Risk Signal.";
const GENERIC_RISKFLOW_SUMMARY =
  /^(?:bullish|bearish|neutral)?\s*catalyst-watch item from RiskFlow\.$/i;

export interface RiskSignal {
  id: string;
  title: string;
  summary: string;
  analysis: string;
  imageUrl?: string | null;
  imageSourceUrl?: string | null;
  imageUnavailableReason?: string | null;
  imageGeneratedAt?: string | null;
  interpretation?: Partial<RiskSignalInterpretation> | null;
  degradationReason?: string | null;
  freshnessDiagnostics?: string | null;
  direction?: "bullish" | "bearish" | "neutral";
  refinementStatus?: "agentic" | "pending-refinement";
  score: number;
  severity: "critical" | "high" | "medium" | "low";
  source: "bulletin" | "catalyst-watch" | "risk-detector";
  relatedHeadlines: string[];
  narrativeThreads: string[];
  generatedAt: string;
  instrument?: string;
  instrumentImpact?: string | null;
  sessionDrift?: string | null;
  scoreRationale?: string | null;
  invalidationRule?: string | null;
  calibrationTags?: string[];
  riskSignalEligible?: boolean;
}

export interface RiskSignalInterpretation {
  whatChanged: string;
  whyItMattersNow: string;
  headwindTailwindRead: string;
  whoLikelyDoesNotKnow: string;
  pricedInRead: string;
  instrumentLabel: string;
  instrumentImpact: string;
  deskImplication: string;
  invalidatesIf: string;
}

export interface RiskSignalPayload {
  signals: RiskSignal[];
  staleSignals?: RiskSignal[];
  generatedAt?: string;
  cached?: boolean;
  stale?: boolean;
  freshnessStatus?: "fresh" | "empty" | "stale-cache" | "generation-error";
  inputCounts?: { bulletins: number; catalysts: number };
  diagnostics?: { staleGeneratedAt?: string | null; reason?: string };
}

export interface CachedSignals {
  signals: RiskSignal[];
  generatedAt: string | null;
  stale: boolean;
  freshnessStatus: RiskSignalPayload["freshnessStatus"];
  inputCounts?: RiskSignalPayload["inputCounts"];
  diagnostics?: RiskSignalPayload["diagnostics"];
}

export const SOURCE_LABEL: Record<string, string> = {
  bulletin: "Bulletin",
  "catalyst-watch": "Catalyst",
  "risk-detector": "Systemic",
};

export function scoreColor(score: number): string {
  if (score >= 8) return "var(--fintheon-bearish)";
  if (score >= 6) return "#f97316";
  if (score >= 4) return "var(--fintheon-accent)";
  return "var(--fintheon-muted)";
}

export function isFreshGenerated(generatedAt: string | null | undefined) {
  if (!generatedAt) return false;
  const generatedMs = new Date(generatedAt).getTime();
  return (
    Number.isFinite(generatedMs) && Date.now() - generatedMs < STALE_AFTER_MS
  );
}

export function formatAge(generatedAt: string | null | undefined): string {
  if (!generatedAt) return "unknown";
  const hours = Math.max(
    0,
    (Date.now() - new Date(generatedAt).getTime()) / 3_600_000,
  );
  if (hours < 1) return "just now";
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function emptyCachedSignals(): CachedSignals {
  return {
    signals: [],
    generatedAt: null,
    stale: false,
    freshnessStatus: "empty",
  };
}

export function displayRiskSignalAnalysis(signal: RiskSignal): string {
  const analysis = signal.analysis.trim();
  if (!analysis || isPlaceholderRiskSignalAnalysis(analysis)) {
    return buildFullRiskSignalText(signal);
  }
  return analysis;
}

export function isPendingRiskSignalRefinement(signal: RiskSignal): boolean {
  return (
    signal.refinementStatus === "pending-refinement" ||
    isPlaceholderRiskSignalAnalysis(signal.analysis)
  );
}

function buildFullRiskSignalText(signal: RiskSignal): string {
  const headline =
    signal.relatedHeadlines.find((item) => item.trim().length > 0)?.trim() ??
    signal.title.trim();
  const summary = signal.summary.trim();
  if (!headline) return summary || signal.analysis;
  if (
    summary &&
    !GENERIC_RISKFLOW_SUMMARY.test(summary) &&
    !sameSignalText(headline, summary)
  ) {
    return `${headline} ${summary}`;
  }
  return headline;
}

function isPlaceholderRiskSignalAnalysis(value: string): boolean {
  return (
    value.includes(LEGACY_FALLBACK_COPY) ||
    value.includes(PENDING_REFINEMENT_COPY)
  );
}

function sameSignalText(left: string, right: string): boolean {
  return normalizeSignalText(left) === normalizeSignalText(right);
}

function normalizeSignalText(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[.!?]+$/g, "")
    .trim();
}

export function buildRiskSignalInterpretation(
  signal: RiskSignal,
): RiskSignalInterpretation {
  const supplied = signal.interpretation ?? {};
  const direction = signal.direction ?? "neutral";
  const source = SOURCE_LABEL[signal.source] ?? "RiskFlow";
  const instrument = signal.instrument ?? "/NQ";
  const headlines = signal.relatedHeadlines.slice(0, 2).join(" | ");
  const baseSummary = signal.summary || signal.analysis;
  const impact =
    direction === "bullish"
      ? "tailwind"
      : direction === "bearish"
        ? "headwind"
        : "mixed read";

  const instrumentImpact =
    signal.instrumentImpact ??
    supplied.instrumentImpact ??
    supplied.deskImplication ??
    "Minor catalyst, no new changes to report but could be part of a larger narrative. Watch for related headlines that confirm a market-moving transmission path.";

  return {
    whatChanged:
      supplied.whatChanged ??
      `${source} promoted this into a ${signal.score.toFixed(1)} risk signal from the current desk window.`,
    whyItMattersNow:
      supplied.whyItMattersNow ??
      `${baseSummary} The timing matters because the signal is competing with current session positioning, not just background news.`,
    headwindTailwindRead:
      supplied.headwindTailwindRead ??
      `Current read: ${impact}. Treat follow-through as conditional on the next liquidity and volatility response.`,
    whoLikelyDoesNotKnow:
      supplied.whoLikelyDoesNotKnow ??
      "Late allocators and headline-only participants may not have connected this to the active desk window yet.",
    pricedInRead:
      supplied.pricedInRead ??
      (signal.score >= 7
        ? "Partly priced, but the second-order path is still live."
        : "Not fully priced unless the related headlines have already moved index beta."),
    instrumentLabel: instrument,
    instrumentImpact,
    deskImplication: supplied.deskImplication ?? instrumentImpact,
    invalidatesIf:
      supplied.invalidatesIf ??
      signal.invalidationRule ??
      defaultInvalidation(signal, instrument, headlines),
  };
}

function defaultInvalidation(
  signal: RiskSignal,
  instrument: string,
  headlines: string,
): string {
  const text = normalizeSignalText(
    [signal.title, signal.summary, signal.analysis, headlines].join(" "),
  );
  if (/\bukmto\b|shipping|maritime|cargo vessel|commercial vessel/.test(text)) {
    return "The headline is reported as false or additional headlines mentioning UKMTO contradict shipping stress or worries.";
  }
  if (headlines) {
    return `The headline is reported as false or follow-on headlines contradict the claimed ${instrument} market transmission.`;
  }
  return `The headline is reported as false, or price, volatility, and follow-on headlines fail to confirm ${instrument} impact during the active window.`;
}

export function imageUnavailableCopy(signal: RiskSignal): string {
  if (signal.imageUrl) return "";
  return (
    signal.imageUnavailableReason ??
    "Image enrichment pending or unavailable for this Risk Signal."
  );
}
